import React, { useState, useMemo } from 'react';
import DashBoardDialog from './DashBoardDialog';
import dayjs from 'dayjs';

const PRIORITY_DISPLAY = {
  1: { label: '긴급', badgeClass: 'bg-red-50 text-red-600' },
  2: { label: '보통', badgeClass: 'bg-yellow-50 text-yellow-600' },
  3: { label: '낮음', badgeClass: 'bg-gray-50 text-gray-500' },
};

/** statusCd: '10' 대기, '20' 진행중, '90' 완료 (DashBoard STATUS_DISPLAY와 동일) */
const STATUS_DISPLAY = {
  10: {
    label: '대기',
    color: 'bg-gray-100 text-gray-600 border-gray-200',
  },
  20: {
    label: '진행중',
    color: 'bg-amber-100 text-amber-700 border-amber-200',
  },
  90: {
    label: '완료',
    color: 'bg-green-100 text-green-700 border-green-200',
  },
};

const ResolveStatusInfo = (statusCd) => {
  const n = Number(statusCd);
  const key = Number.isFinite(n) ? n : 10;
  return STATUS_DISPLAY[key] ?? STATUS_DISPLAY[10];
};

function CompleteWorkListDialog({
  open = false,
  onClose = () => {},
  items = [],
  /** TaskFormDialog와 동일: workId로 `handleDeleteWork` 호출 */
  onDelete,
}) {
  const [searchText, setSearchText] = useState('');

  const filtered = useMemo(() => {
    if (!searchText.trim()) return items;
    const keyword = searchText.trim().toLowerCase();
    return items.filter(
      (item) =>
        (item.title || '').toLowerCase().includes(keyword) ||
        (item.contents || '').toLowerCase().includes(keyword)
    );
  }, [items, searchText]);

  const fields = [
    {
      label: '',
      span: 2,
      value: (
        <div className="w-full flex flex-col gap-3">
          {/* 검색 */}
          <div className="flex items-center gap-2">
            <span className="text-sm font-bold text-[#2c3e50] shrink-0">
              검색어
            </span>
            <input
              type="text"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder="제목, 내용으로 검색"
              className="flex-1 px-3 py-2 text-sm border border-[#e1e8ed] rounded-lg focus:outline-none focus:border-[#77b054] transition-colors"
            />
          </div>

          {/* 테이블 */}
          <div className="border border-[#e1e8ed] rounded-lg overflow-hidden">
            <div
              className={`grid px-3 py-2.5 bg-gray-100 border-b border-[#e1e8ed] ${
                typeof onDelete === 'function'
                  ? 'grid-cols-[3rem_2fr_3fr_5rem_6rem_5rem_4.5rem]'
                  : 'grid-cols-[3rem_2fr_3fr_5rem_6rem_5rem]'
              }`}
            >
              <span className="text-[11px] font-bold text-[#7f8c8d] text-center">
                No
              </span>
              <span className="text-[11px] font-bold text-[#7f8c8d] text-left">
                제목
              </span>
              <span className="text-[11px] font-bold text-[#7f8c8d] text-left">
                내용
              </span>
              <span className="text-[11px] font-bold text-[#7f8c8d] text-center">
                우선순위
              </span>
              <span className="text-[11px] font-bold text-[#7f8c8d] text-center">
                완료일자
              </span>
              <span className="text-[11px] font-bold text-[#7f8c8d] text-center">
                상태
              </span>
              {typeof onDelete === 'function' && (
                <span className="text-[11px] font-bold text-[#7f8c8d] text-center">
                  삭제
                </span>
              )}
            </div>

            <div
              className="h-80 overflow-y-auto divide-y divide-[#e1e8ed]/60 bg-white custom-scrollbar"
              style={{
                scrollbarWidth: 'thin',
                scrollbarColor: '#e1e8ed transparent',
              }}
            >
              {filtered.length === 0 ? (
                <div className="flex items-center justify-center h-full text-sm text-gray-400">
                  완료된 업무가 없습니다.
                </div>
              ) : (
                filtered.map((item, index) => {
                  const priorityInfo =
                    PRIORITY_DISPLAY[item.priorityBit] ?? PRIORITY_DISPLAY[3];
                  const statusInfo = ResolveStatusInfo(item.statusCd);
                  return (
                    <div
                      key={item.workId ?? index}
                      className={`grid px-3 py-3.5 items-center hover:bg-gray-50 transition-colors ${
                        typeof onDelete === 'function'
                          ? 'grid-cols-[3rem_2fr_3fr_5rem_6rem_5rem_4.5rem]'
                          : 'grid-cols-[3rem_2fr_3fr_5rem_6rem_5rem]'
                      }`}
                    >
                      <span className="text-sm text-gray-400 text-center">
                        {index + 1}
                      </span>
                      <span className="text-sm font-bold text-[#2c3e50] truncate pr-2">
                        {item.title || '-'}
                      </span>
                      <span className="text-sm text-gray-500 truncate pr-4">
                        {item.contents || '-'}
                      </span>
                      <div className="flex justify-center">
                        <span
                          className={`px-2 py-1 text-[11px] font-bold rounded-md ${priorityInfo.badgeClass}`}
                        >
                          {priorityInfo.label}
                        </span>
                      </div>
                      <span className="text-xs text-gray-500 text-center">
                        {item.complDt
                          ? dayjs(item.complDt).format('YYYY-MM-DD')
                          : '-'}
                      </span>
                      <div className="flex justify-center">
                        <span
                          className={`px-2 py-1 text-[11px] font-bold rounded-full border ${statusInfo.color}`}
                        >
                          {statusInfo.label}
                        </span>
                      </div>
                      {typeof onDelete === 'function' && (
                        <div className="flex justify-center">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (item.workId != null && item.workId !== '') {
                                onDelete(item.workId);
                              }
                            }}
                            className="px-3 py-1.5 rounded-lg text-xs font-bold bg-white text-red-500 border border-red-200 hover:bg-red-50 transition-colors"
                          >
                            삭제
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      ),
    },
  ];

  return (
    <DashBoardDialog
      open={open}
      onClose={onClose}
      titleIcon={
        <div className="w-8 h-8 rounded-lg bg-green-50 flex items-center justify-center text-[#77b054]">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="w-4 h-4"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="20 6 9 17 4 12" />
          </svg>
        </div>
      }
      title="업무 목록 전체보기"
      subTitle="*조회 및 삭제 가능 (수정은 대시보드 업무 카드에서)"
      fields={fields}
      size="xl"
      actions={
        <div className="flex justify-end">
          <button
            onClick={onClose}
            className="px-5 py-2 rounded-lg text-sm font-bold bg-white text-[#7f8c8d] border border-[#e1e8ed] hover:bg-gray-100 transition-colors"
          >
            닫기
          </button>
        </div>
      }
    />
  );
}

export default CompleteWorkListDialog;
